import _ from "lodash";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  useCustomQueryParams,
  useModalConfirm,
  useReportId,
} from "@ryde/redux";
import {
  ConfirmResult,
  Log,
  reportFiltersWithValuesAF,
  rideFilterSearchAtom,
} from "@ryde/shared";

import { clearValuesFilterStoreObjReports } from "./report-filter.helpers";

export const useTableFiltersActionsReports = () => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const { reportId } = useReportId();
  const [filtersObj, setFiltersObj] = useRecoilState(
    reportFiltersWithValuesAF(reportId),
  );

  const setTableSearchValue = useSetRecoilState(rideFilterSearchAtom(reportId));
  const { setFilterSearchQsByValue } = useCustomQueryParams();
  const handleAddFilter = useEvent((templateKey: string | undefined) => {
    if (!templateKey) {
      return;
    }

    setFiltersObj(prev => {
      const newFilters = { ...(prev ?? {}), [templateKey]: null };
      Log.debug("Handle add filter, updated filters:", newFilters);
      setFilterSearchQsByValue(newFilters);

      return newFilters;
    });
  });

  const handleClearFilters = useEvent(async () => {
    setTableSearchValue("");
    setFiltersObj(prev => {
      const clearedObj = clearValuesFilterStoreObjReports(prev ?? {});
      Log.debug(
        "Clear filters handler called For reports, updated store object:",
        clearedObj,
      );
      setFilterSearchQsByValue(clearedObj);
      return clearedObj;
    });
  });

  const handleRemoveFilter = useEvent(async (templateKey: string) => {
    /* prompt confirmation only if filter has value(s) */
    if (filtersObj && !_.isEmpty(filtersObj[templateKey])) {
      const res = await confirmDialogResult({
        title: t("general.REMOVE_FILTER_CONFIRM"),
        message: t("general.REMOVE_FILTER_CONFIRM_MESSAGE"),
      });

      if (res !== ConfirmResult.OK) {
        Log.infoAndToast(t("errors.removeFilterCanceledByUser"), templateKey);
        return;
      }
    }

    Log.debug(
      `Remove filter handler called for ${templateKey}, current filters: `,
      {
        ...(filtersObj ?? {}),
      },
    );
    setFiltersObj(prev => {
      const updatedFilterObj = _.omit(prev ?? {}, String(templateKey));
      setFilterSearchQsByValue(updatedFilterObj);
      return updatedFilterObj;
    });
  });

  const setValuesToFilter = useCallback(
    (templateKey: string, values: number[] | string[]) => {
      setFiltersObj(prev => {
        const updatedFilter = {
          ...(prev ?? {}),
          [templateKey]: values,
        };
        Log.debug(
          `Set values to filter  called for template key ${templateKey}, updated filter:`,
          updatedFilter,
        );
        setFilterSearchQsByValue(updatedFilter);
        return updatedFilter;
      });
    },
    [setFilterSearchQsByValue, setFiltersObj],
  );

  return {
    handleAddFilter,
    handleRemoveFilter,
    handleClearFilters,
    setValuesToFilter,
  };
};
